import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    q: 'What is MindVrik?',
    a: 'MindVrik is an AI-supported career guidance and psychological assessment platform designed for students and parents. It combines aptitude mapping, stream selection, learning-style insights, intelligent AI reports, and human expert counseling into a single guidance experience.',
  },
  {
    q: 'Is this therapy or medical diagnosis?',
    a: 'No. MindVrik is an advisory platform, not a therapy clinic and not a medical diagnostic service. Our assessments and reports are guidance-oriented, designed to help with career clarity, stream and subject selection, learning strategies, and personal development. They do not replace professional psychiatric or medical evaluation.',
  },
  {
    q: 'Are the assessment reports considered medical advice?',
    a: 'Absolutely not. All reports generated on MindVrik are advisory in nature. They are educational and developmental guidance tools. For clinical or medical concerns, we always recommend consulting a licensed medical professional.',
  },
  {
    q: 'Who is this platform designed for?',
    a: 'MindVrik serves three primary groups: students aged 13-25 seeking clarity on their academic and career path; parents who want to understand and guide their child\'s development; and professional counselors looking to join a trusted, structured guidance platform.',
  },
  {
    q: 'How long do the assessments take?',
    a: 'Most assessments generally take 1.5-2 hours, depending on the type and depth. You can pause and continue later within your validity period, so there is no pressure to complete everything in a single sitting. Progress is automatically saved.',
  },
  {
    q: 'Can a parent take an assessment on behalf of their child?',
    a: 'Yes. Parents can create an account and register their child\'s profile. During assessment setup, parents can provide the necessary context. Our reports include a dedicated Parent Guidance section written specifically for caregivers.',
  },
  {
    q: 'How does the counseling booking work?',
    a: 'After completing your assessment and receiving your report, you can book a counseling session directly from your dashboard. Choose your preferred counselor, select an available time slot, and receive confirmation via email. Sessions are conducted online.',
  },
  {
    q: 'Is my data secure and private?',
    a: 'Yes. All data on MindVrik is encrypted end-to-end. We follow strict data privacy standards and never share personal information with third parties without explicit consent. You can delete your data at any time from your account settings.',
  },
  {
    q: 'What plan should I choose?',
    a: 'For an individual first-time user, we recommend starting with the Individual Assessment. Schools and student groups can choose the Standard Diagnostic plan, while students who need sustained career planning can choose the 3-Month Subscription.',
  },
  {
    q: 'Can counselors apply to join the platform?',
    a: 'Yes. We welcome qualified career counselors, clinical psychologists, and workshop experts to apply. Applicants should hold relevant professional credentials. Visit "Join Our Organisation" to begin your application and learn about onboarding.',
  },
];

const FAQ = () => {
  const [ref, visible] = useScrollReveal();
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      style={{ padding: '100px 24px', background: '#F9F9F7', position: 'relative' }}
      data-testid="faq-section"
      ref={ref}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">FAQ</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Clear Answers to<br />
            <span style={{ fontWeight: 300, color: '#64748B' }}>Common Questions</span>
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${Math.min(i + 1, 7)} ${visible ? 'visible' : ''}`}
              data-testid={`faq-item-${i}`}
              style={{
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                border: openIndex === i ? '1.5px solid rgba(42,157,143,0.25)' : '1.5px solid transparent',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.25s ease',
                boxShadow: openIndex === i ? '0 8px 32px rgba(42,157,143,0.1)' : '0 2px 8px rgba(0,0,0,0.03)',
              }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer',
                  gap: '16px', textAlign: 'left',
                }}
                data-testid={`faq-toggle-${i}`}
              >
                <span style={{
                  fontFamily: 'Manrope', fontWeight: 600, fontSize: '15px',
                  color: openIndex === i ? '#2A9D8F' : '#1A202C',
                  transition: 'color 0.2s ease', lineHeight: 1.45,
                }}>
                  {faq.q}
                </span>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                  background: openIndex === i ? 'rgba(42,157,143,0.12)' : 'rgba(0,0,0,0.04)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: openIndex === i ? '#2A9D8F' : '#94A3B8',
                  transition: 'all 0.2s ease',
                }}>
                  {openIndex === i ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </div>
              </button>

              <div style={{
                maxHeight: openIndex === i ? '320px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <div style={{
                  padding: '0 24px 20px',
                  fontSize: '14px', color: '#64748B', lineHeight: 1.78,
                  fontFamily: 'Inter',
                }}>
                  {faq.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
